import DocumentRequestModal from "./DocumentRequestModal";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy, writeBatch, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import INIT_CLIENTS from "./clientsData";

const SERVICE_OPTIONS = ["Bookkeeping","Tax Preparation","CFO Advisory","Bookkeeping + CFO Bundle","Full Service","Cleanup / Catchup","QBO Setup","Tax & Bookkeeping","One-time Project"];
const STATUS_OPTIONS = ["Active","Inactive","Archived"];

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px",marginBottom:16},
  input:{padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",color:"#1a1a18"},
  btn:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnSm:{fontSize:11,padding:"3px 9px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"},
  th:{textAlign:"left",padding:"7px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12,whiteSpace:"nowrap"},
  td:{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",verticalAlign:"middle",fontSize:13},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 8px",borderRadius:20,background:bg,color:c,fontWeight:500}),
};

const statusColors = {
  Active:["#EAF3DE","#3B6D11"],
  Inactive:["#FAEEDA","#854F0B"],
  Archived:["#f5f5f0","#888"],
};

const serviceColors = {
  "Bookkeeping":["#E6F1FB","#185FA5"],
  "Tax Preparation":["#E1F5EE","#0F6E56"],
  "CFO Advisory":["#FAECE7","#993C1D"],
  "Bookkeeping + CFO Bundle":["#EEEDFE","#534AB7"],
  "Full Service":["#FAEEDA","#854F0B"],
  "Cleanup / Catchup":["#E1F5EE","#085041"],
  "QBO Setup":["#E6F1FB","#0C447C"],
  "Tax & Bookkeeping":["#EAF3DE","#27500A"],
  "One-time Project":["#f5f5f0","#555"],
};

function formatPhone(val){
  const digits=val.replace(/\D/g,"").slice(0,10);
  if(digits.length===0) return "";
  if(digits.length<=3) return "("+digits;
  if(digits.length<=6) return "("+digits.slice(0,3)+") "+digits.slice(3);
  return "("+digits.slice(0,3)+") "+digits.slice(3,6)+"-"+digits.slice(6);
}

function PortalStatusBadge({client}){
  if(client.portalAccepted) return <span style={{fontSize:10,padding:"2px 8px",borderRadius:20,background:"#EAF3DE",color:"#3B6D11",fontWeight:500,border:"0.5px solid #B5D8A4"}}>✉ Accepted</span>;
  if(client.portalInvited) return <span style={{fontSize:10,padding:"2px 8px",borderRadius:20,background:"#FAEEDA",color:"#854F0B",fontWeight:500,border:"0.5px solid #EF9F27"}}>✉ Pending</span>;
  return <span style={{fontSize:10,padding:"2px 8px",borderRadius:20,background:"#f5f5f0",color:"#aaa",fontWeight:400,border:"0.5px solid #e0dfd8"}}>✉ Not Invited</span>;
}

function Avatar({initials,bg,color,size=28}){
  return <div style={{width:size,height:size,borderRadius:"50%",background:bg||"#E6F1FB",color:color||"#185FA5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.38,fontWeight:500,flexShrink:0}}>{initials}</div>;
}

function ClientModal({client,onClose,onSave}){
  const [form,setForm]=useState({...client});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const toggleService=(s)=>{
    const svcs=(form.services||[]).includes(s)?(form.services||[]).filter(x=>x!==s):[...(form.services||[]),s];
    set("services",svcs);
  };
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:520,maxHeight:"85vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{fontSize:14,fontWeight:500}}>{client.firestoreId?"Edit Client":"Add New Client"}</div>
          <button style={{...S.btn,padding:"3px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Client / Company name</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.name||""} onChange={e=>set("name",e.target.value)}/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Contact person</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.contact||""} onChange={e=>set("contact",e.target.value)}/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Email</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.email||""} onChange={e=>set("email",e.target.value)}/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Secondary Email <span style={{fontWeight:400,color:"#bbb"}}>(optional · 2nd portal user)</span></div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.email2||""} onChange={e=>set("email2",e.target.value)} placeholder="e.g. spouse@email.com"/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Phone</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.phone||""} onChange={e=>set("phone",formatPhone(e.target.value))} placeholder="(941) 000-0000" maxLength={14}/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>City / State</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.cityState||""} onChange={e=>set("cityState",e.target.value)}/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Status</div>
            <select style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.status||"Active"} onChange={e=>set("status",e.target.value)}>
              {STATUS_OPTIONS.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:"#888",marginBottom:6}}>Services provided</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {SERVICE_OPTIONS.map(s=>{
              const active=(form.services||[]).includes(s);
              const [bg,c]=serviceColors[s]||["#f5f5f0","#555"];
              return <div key={s} onClick={()=>toggleService(s)} style={{fontSize:11,padding:"4px 10px",borderRadius:20,background:active?bg:"#f5f5f0",color:active?c:"#aaa",border:"0.5px solid "+(active?c:"#ddd"),cursor:"pointer",fontWeight:active?500:400}}>{s}</div>;
            })}
          </div>
        </div>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:11,color:"#888",marginBottom:3}}>Notes</div>
          <textarea style={{...S.input,width:"100%",boxSizing:"border-box",resize:"vertical"}} rows={3} value={form.notes||""} onChange={e=>set("notes",e.target.value)} placeholder="Any notes about this client..."/>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={()=>onSave(form)}>Save Client</button>
        </div>
      </div>
    </div>
  );
}

function QuickInvoiceModal({client, onClose, onSave}){
  const SERVICE_OPTIONS_INV = ["Bookkeeping","Tax Preparation","CFO Advisory","Bookkeeping + CFO Bundle","Full Service","Cleanup / Catchup","QBO Setup","One-time Project"];
  const [form, setForm] = useState({client:client.name, service:"", amount:"", due:"", status:"Draft"});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:440}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:14,fontWeight:500}}>Quick Invoice</div>
            <div style={{fontSize:11,color:"#888",marginTop:2}}>{client.name}</div>
          </div>
          <button style={{...S.btn,padding:"3px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Service</div>
            <select style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.service} onChange={e=>set("service",e.target.value)}>
              <option value="">Select service...</option>
              {SERVICE_OPTIONS_INV.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Amount</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.amount} onChange={e=>set("amount",e.target.value)} placeholder="e.g. $350"/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Due date</div><input style={{...S.input,width:"100%",boxSizing:"border-box"}} type="date" value={form.due} onChange={e=>set("due",e.target.value)}/></div>
          <div><div style={{fontSize:11,color:"#888",marginBottom:3}}>Status</div>
            <select style={{...S.input,width:"100%",boxSizing:"border-box"}} value={form.status} onChange={e=>set("status",e.target.value)}>
              <option>Draft</option><option>Sent</option><option>Paid</option><option>Overdue</option>
            </select>
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={()=>{if(!form.amount){alert("Please enter an amount.");return;}onSave(form);}}>Create Invoice</button>
        </div>
      </div>
    </div>
  );
}

function BatchServicesModal({count, onClose, onSave}){
  const [selected, setSelected] = useState([]);
  const toggle = (s) => setSelected(prev=>prev.includes(s)?prev.filter(x=>x!==s):[...prev,s]);
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:460}}>
        <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Assign Services</div>
        <div style={{fontSize:12,color:"#888",marginBottom:14}}>Apply to {count} selected client{count!==1?"s":""}. This will replace their current services.</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:18}}>
          {SERVICE_OPTIONS.map(s=>{
            const active=selected.includes(s);
            const [bg,c]=serviceColors[s]||["#f5f5f0","#555"];
            return <div key={s} onClick={()=>toggle(s)} style={{fontSize:11,padding:"5px 12px",borderRadius:20,background:active?bg:"#f5f5f0",color:active?c:"#aaa",border:"0.5px solid "+(active?c:"#ddd"),cursor:"pointer",fontWeight:active?500:400}}>{s}</div>;
          })}
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={()=>{if(selected.length===0){alert("Select at least one service.");return;}onSave(selected);}}>Apply to {count} Client{count!==1?"s":""}</button>
        </div>
      </div>
    </div>
  );
}

function ActionDropdown({client, onEdit, onDelete, onInvite, onInvoice, onDocRequest, onMessage, inviteStatus}){
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(()=>{
    const handler = (e)=>{ if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[]);

  const item = (label, color, onClick) => (
    <div onClick={()=>{setOpen(false);onClick();}} style={{padding:"8px 14px",fontSize:12,color:color||"#1a1a18",cursor:"pointer",whiteSpace:"nowrap",borderBottom:"0.5px solid #f5f5f0"}}
      onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {label}
    </div>
  );

  return (
    <div style={{display:"flex",gap:0,position:"relative"}} ref={ref}>
      <button style={{...S.btnSm,borderRadius:"6px 0 0 6px",borderRight:"none"}} onClick={onEdit}>Edit</button>
      <button style={{...S.btnSm,borderRadius:"0 6px 6px 0",padding:"3px 6px",borderLeft:"0.5px solid #e0dfd8"}} onClick={()=>setOpen(o=>!o)}>▾</button>
      {open&&(
        <div style={{position:"absolute",right:0,top:"100%",marginTop:2,background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:8,boxShadow:"0 4px 20px rgba(0,0,0,0.12)",zIndex:100,minWidth:160,overflow:"hidden"}}>
          {item("✏️ Edit client", "#1a1a18", onEdit)}
          {item("💳 Quick Invoice", "#185FA5", onInvoice)}
          {item("📄 Request Documents", "#534AB7", onDocRequest)}
          {item("💬 Send Message", "#185FA5", onMessage)}
          {item(client.portalInvited?"✉ Resend Portal Invite":"✉ Invite to Portal", "#534AB7", onInvite)}
          {item("📋 Mark Inactive", "#854F0B", ()=>onEdit("inactive"))}
          {item("🗄 Archive", "#888", ()=>onEdit("archive"))}
          <div onClick={()=>{setOpen(false);onDelete();}} style={{padding:"8px 14px",fontSize:12,color:"#A32D2D",cursor:"pointer",whiteSpace:"nowrap",background:"transparent"}}
            onMouseEnter={e=>e.currentTarget.style.background="#FCEBEB"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            🗑 Delete Client
          </div>
        </div>
      )}
    </div>
  );
}

export default function Clients({ assignedClients, onNavigateToMessages }){
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [serviceFilter, setServiceFilter] = useState("All Services");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [modal, setModal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [inviteStatus, setInviteStatus] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchAction, setBatchAction] = useState("");
  const [showBatchServices, setShowBatchServices] = useState(false);
  const [quickInvoiceClient, setQuickInvoiceClient] = useState(null);
  const [docRequestClient, setDocRequestClient] = useState(null);
  const [messageClient, setMessageClient] = useState(null);

  useEffect(()=>{
    const q=query(collection(db,"clients"),orderBy("name"));
    const unsub=onSnapshot(q,snap=>{
      const docs=snap.docs.map(d=>({...d.data(),firestoreId:d.id}));
      setClients(docs);
      setImported(docs.length>0);
      setLoading(false);
    },err=>{console.error(err);setLoading(false);});
    return ()=>unsub();
  },[]);

  const importClients=async()=>{
    if(!window.confirm(`Import all ${INIT_CLIENTS.length} clients from QBO to Firestore? This only needs to be done once.`)) return;
    setImporting(true);
    try{
      const existing=await getDocs(collection(db,"clients"));
      if(existing.size>0){alert("Clients already imported! You have "+existing.size+" clients in Firestore.");setImporting(false);return;}
      let batch=writeBatch(db);let count=0;const batches=[];
      for(const client of INIT_CLIENTS){
        const ref=doc(collection(db,"clients"));
        const {id,...data}=client;
        batch.set(ref,{...data,importedAt:new Date().toISOString()});
        count++;
        if(count===499){batches.push(batch);batch=writeBatch(db);count=0;}
      }
      batches.push(batch);
      for(const b of batches) await b.commit();
      alert(`✓ ${INIT_CLIENTS.length} clients imported successfully!`);
    }catch(e){alert("Import failed: "+e.message);}
    setImporting(false);
  };

  const saveClient=async(form)=>{
    const{firestoreId,...data}=form;
    if(!data.initials){const words=(data.name||"").trim().split(" ");data.initials=((words[0]?.[0]||"")+(words[1]?.[0]||"")).toUpperCase();}
    if(!data.avatarBg) data.avatarBg="#E6F1FB";
    if(!data.avatarColor) data.avatarColor="#185FA5";
    if(!data.services) data.services=[];
    try{
      if(firestoreId){await updateDoc(doc(db,"clients",firestoreId),data);}
      else{await addDoc(collection(db,"clients"),{...data,createdAt:new Date().toISOString()});}
      setModal(null);
    }catch(e){alert("Save failed: "+e.message);}
  };

  const deleteClient=async(firestoreId,name)=>{
    if(!window.confirm(`Delete "${name}" from your client list? This cannot be undone.`)) return;
    try{await deleteDoc(doc(db,"clients",firestoreId));}
    catch(e){alert("Delete failed: "+e.message);}
  };

  const archiveClient=async(firestoreId,status)=>{
    try{await updateDoc(doc(db,"clients",firestoreId),{status});}catch(e){}
  };

  const inviteClient=async(client)=>{
    if(!client.email){alert("This client has no email address. Please add their email first.");return;}
    const emailList = client.email + (client.email2 ? "\n  Secondary: "+client.email2 : "");
    const confirmed=window.confirm("Send portal invite to "+client.name+"?\n\nEmail: "+emailList+"\n\nThey will receive a branded email with a link to set up their password.");
    if(!confirmed) return;
    setInviteStatus(s=>({...s,[client.firestoreId]:"sending"}));
    try{
      const functions = getFunctions();
      const inviteClientFn = httpsCallable(functions, "inviteClient");
      await inviteClientFn({email:client.email, email2:client.email2||null, name:client.name, firestoreId:client.firestoreId});
      setInviteStatus(s=>({...s,[client.firestoreId]:"sent"}));
      alert("✓ Invite sent to "+client.email+"!");
    }catch(e){
      setInviteStatus(s=>({...s,[client.firestoreId]:"error"}));
      alert("Failed to send invite: "+e.message);
    }
  };


  const saveQuickInvoice=async(form)=>{
    try{
      const num="#"+String(1000+Math.floor(Math.random()*9000));
      await addDoc(collection(db,"invoices"),{...form,num,createdAt:new Date().toISOString()});
      setQuickInvoiceClient(null);
      alert("✓ Invoice "+num+" created for "+form.client+"!");
    }catch(e){alert("Failed: "+e.message);}
  };

  // Batch actions
  const toggleSelect=(id)=>{
    setSelectedIds(prev=>{
      const next=new Set(prev);
      next.has(id)?next.delete(id):next.add(id);
      return next;
    });
  };
  const toggleSelectAll=()=>{
    if(selectedIds.size===filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(c=>c.firestoreId)));
  };

  const executeBatchAction=async(action)=>{
    const selected=clients.filter(c=>selectedIds.has(c.firestoreId));
    if(selected.length===0) return;
    if(action==="inactive"){
      if(!window.confirm("Mark "+selected.length+" clients as Inactive?")) return;
      for(const c of selected) await updateDoc(doc(db,"clients",c.firestoreId),{status:"Inactive"});
      setSelectedIds(new Set());
    } else if(action==="archive"){
      if(!window.confirm("Archive "+selected.length+" clients?")) return;
      for(const c of selected) await updateDoc(doc(db,"clients",c.firestoreId),{status:"Archived"});
      setSelectedIds(new Set());
    } else if(action==="active"){
      for(const c of selected) await updateDoc(doc(db,"clients",c.firestoreId),{status:"Active"});
      setSelectedIds(new Set());
    } else if(action==="delete"){
      if(!window.confirm("Permanently delete "+selected.length+" clients? This cannot be undone.")) return;
      for(const c of selected) await deleteDoc(doc(db,"clients",c.firestoreId));
      setSelectedIds(new Set());
    } else if(action==="services"){
      setShowBatchServices(true);
    }
    setBatchAction("");
  };

  const applyBatchServices=async(services)=>{
    const selected=clients.filter(c=>selectedIds.has(c.firestoreId));
    for(const c of selected) await updateDoc(doc(db,"clients",c.firestoreId),{services});
    setSelectedIds(new Set());
    setShowBatchServices(false);
  };

  const visibleClients=assignedClients?clients.filter(c=>assignedClients.includes(c.name)):clients;

  const filtered=useMemo(()=>{
    let list=[...visibleClients];
    if(search) list=list.filter(c=>c.name.toLowerCase().includes(search.toLowerCase())||((c.email||"").toLowerCase().includes(search.toLowerCase()))||((c.contact||"").toLowerCase().includes(search.toLowerCase())));
    if(statusFilter!=="All") list=list.filter(c=>c.status===statusFilter);
    if(serviceFilter!=="All Services") list=list.filter(c=>(c.services||[]).includes(serviceFilter));
    list.sort((a,b)=>{
      let av=(a[sortBy]||"").toLowerCase(),bv=(b[sortBy]||"").toLowerCase();
      return sortDir==="asc"?(av>bv?1:-1):(av<bv?1:-1);
    });
    return list;
  },[visibleClients,search,statusFilter,serviceFilter,sortBy,sortDir]);

  const counts=useMemo(()=>({
    All:visibleClients.length,
    Active:visibleClients.filter(c=>c.status==="Active").length,
    Inactive:visibleClients.filter(c=>c.status==="Inactive").length,
    Archived:visibleClients.filter(c=>c.status==="Archived").length,
  }),[visibleClients]);

  const sort=(col)=>{if(sortBy===col) setSortDir(d=>d==="asc"?"desc":"asc");else{setSortBy(col);setSortDir("asc");}};
  const sortIcon=(col)=>sortBy===col?(sortDir==="asc"?" ↑":" ↓"):" ↕";
  const newClient={name:"",email:"",phone:"",contact:"",cityState:"",initials:"",avatarBg:"#E6F1FB",avatarColor:"#185FA5",status:"Active",services:[],notes:""};

  if(loading) return <div style={{textAlign:"center",padding:"40px 0",color:"#888",fontSize:13}}>Loading clients...</div>;

  return (
    <div>
      {modal&&<ClientModal client={modal} onClose={()=>setModal(null)} onSave={saveClient}/>}
      {docRequestClient&&<DocumentRequestModal client={docRequestClient} onClose={()=>setDocRequestClient(null)} currentUserName="Frank Ficsori" isAdmin={true}/>}
      {quickInvoiceClient&&<QuickInvoiceModal client={quickInvoiceClient} onClose={()=>setQuickInvoiceClient(null)} onSave={saveQuickInvoice}/>}
      {docRequestClient&&<DocumentRequestModal client={docRequestClient} onClose={()=>setDocRequestClient(null)} currentUserName="Frank Ficsori" isAdmin={true}/>}
      {showBatchServices&&<BatchServicesModal count={selectedIds.size} onClose={()=>setShowBatchServices(false)} onSave={applyBatchServices}/>}

      {!imported&&clients.length===0&&(
        <div style={{background:"#FAEEDA",border:"0.5px solid #EF9F27",borderRadius:12,padding:"14px 18px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:13,fontWeight:500,color:"#633806"}}>Import your QBO clients to Firestore</div>
            <div style={{fontSize:12,color:"#854F0B",marginTop:2}}>One-time step. All {INIT_CLIENTS.length} clients will sync across all devices.</div>
          </div>
          <button style={{...S.btnPrimary,background:"#854F0B",whiteSpace:"nowrap"}} onClick={importClients} disabled={importing}>{importing?"Importing...":"Import All Clients"}</button>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:500}}>Client CRM</div>
        <button style={S.btnPrimary} onClick={()=>setModal(newClient)}>+ Add Client</button>
      </div>

      {/* Batch Actions Bar */}
      {selectedIds.size>0&&(
        <div style={{background:"#1a1a18",borderRadius:10,padding:"10px 16px",marginBottom:12,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:12,color:"#fff",fontWeight:500}}>{selectedIds.size} client{selectedIds.size!==1?"s":""} selected</span>
          <div style={{flex:1}}/>
          <span style={{fontSize:11,color:"#aaa"}}>Batch actions:</span>
          <button style={{...S.btnSm,background:"#3B6D11",color:"#fff",border:"none",fontSize:11}} onClick={()=>executeBatchAction("active")}>Mark Active</button>
          <button style={{...S.btnSm,background:"#854F0B",color:"#fff",border:"none",fontSize:11}} onClick={()=>executeBatchAction("inactive")}>Mark Inactive</button>
          <button style={{...S.btnSm,background:"#534AB7",color:"#fff",border:"none",fontSize:11}} onClick={()=>executeBatchAction("services")}>Assign Services</button>
          <button style={{...S.btnSm,background:"#555",color:"#fff",border:"none",fontSize:11}} onClick={()=>executeBatchAction("archive")}>Archive</button>
          <button style={{...S.btnSm,background:"#A32D2D",color:"#fff",border:"none",fontSize:11}} onClick={()=>executeBatchAction("delete")}>Delete</button>
          <button style={{...S.btnSm,color:"#aaa",fontSize:11}} onClick={()=>setSelectedIds(new Set())}>✕ Clear</button>
        </div>
      )}

      {/* Status tabs */}
      <div style={{display:"flex",gap:6,marginBottom:12}}>
        {["All","Active","Inactive","Archived"].map(s=>(
          <button key={s} onClick={()=>setStatusFilter(s)} style={{...S.btn,background:statusFilter===s?"#C0111F":"#fff",color:statusFilter===s?"#fff":"#555",border:"0.5px solid "+(statusFilter===s?"#C0111F":"#ccc"),fontWeight:statusFilter===s?500:400}}>
            {s} ({counts[s]||0})
          </button>
        ))}
      </div>

      {/* Back to all + Search + filters */}
      {(search||statusFilter!=="All"||serviceFilter!=="All Services")&&(
        <div style={{marginBottom:10}}>
          <button style={{...S.btn,fontSize:11,color:"#185FA5",borderColor:"#B5D4F4",background:"#E6F1FB"}}
            onClick={()=>{setSearch("");setStatusFilter("All");setServiceFilter("All Services");}}>
            ← Back to All Clients
          </button>
        </div>
      )}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:180,position:"relative"}}>
          <input style={{...S.input,width:"100%",boxSizing:"border-box",paddingRight:search?28:10}} placeholder="Search clients, contacts, emails..." value={search} onChange={e=>setSearch(e.target.value)}/>
          {search&&<div onClick={()=>setSearch("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",cursor:"pointer",color:"#aaa",fontSize:16,lineHeight:1}}>✕</div>}
        </div>
        <select style={{...S.input,width:200}} value={serviceFilter} onChange={e=>setServiceFilter(e.target.value)}>
          <option>All Services</option>
          {SERVICE_OPTIONS.map(s=><option key={s}>{s}</option>)}
        </select>
        <div style={{fontSize:12,color:"#888",alignSelf:"center"}}>{filtered.length} client{filtered.length!==1?"s":""}</div>
      </div>

      <div style={S.card}>
        {clients.length===0?(
          <div style={{textAlign:"center",padding:"32px 0",color:"#aaa",fontSize:13}}>No clients yet. Use the Import button above or click + Add Client.</div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr>
                <th style={{...S.th,width:32}}>
                  <input type="checkbox" checked={selectedIds.size===filtered.length&&filtered.length>0} onChange={toggleSelectAll}/>
                </th>
                <th style={S.th}><span style={{cursor:"pointer"}} onClick={()=>sort("name")}>Client{sortIcon("name")}</span></th>
                <th style={S.th}>Contact</th>
                <th style={S.th}>Services</th>
                <th style={S.th}><span style={{cursor:"pointer"}} onClick={()=>sort("status")}>Status{sortIcon("status")}</span></th>
                <th style={S.th}><span style={{cursor:"pointer"}} onClick={()=>sort("cityState")}>Location{sortIcon("cityState")}</span></th>
                <th style={S.th}>Portal</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c=>(
                <React.Fragment key={c.firestoreId}>
                  <tr style={{background:selectedIds.has(c.firestoreId)?"#fdf8f8":"transparent"}} onClick={()=>setExpandedId(expandedId===c.firestoreId?null:c.firestoreId)}>
                    <td style={S.td} onClick={e=>e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(c.firestoreId)} onChange={()=>toggleSelect(c.firestoreId)}/>
                    </td>
                    <td style={S.td}>
                      <div style={{display:"flex",alignItems:"center",gap:9}}>
                        <Avatar initials={c.initials} bg={c.avatarBg} color={c.avatarColor}/>
                        <div>
                          <div style={{fontWeight:500,fontSize:13}}>{c.name}</div>
                          {c.email&&<div style={{fontSize:11,color:"#888"}}>{c.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={S.td}><div style={{fontSize:12,color:"#666"}}>{c.contact||"—"}</div>{c.phone&&<div style={{fontSize:11,color:"#aaa"}}>{c.phone}</div>}</td>
                    <td style={S.td}>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {(!c.services||c.services.length===0)&&<span style={{fontSize:11,color:"#ccc"}}>None</span>}
                        {(c.services||[]).map(s=>{const[bg,col]=serviceColors[s]||["#f5f5f0","#555"];return <span key={s} style={S.badge(bg,col)}>{s}</span>;})}
                      </div>
                    </td>
                    <td style={S.td}><span style={S.badge(...(statusColors[c.status]||["#f5f5f0","#888"]))}>{c.status}</span></td>
                    <td style={{...S.td,color:"#888",fontSize:12}}>{c.cityState||"—"}</td>
                    <td style={S.td} onClick={e=>e.stopPropagation()}><PortalStatusBadge client={c}/></td>
                    <td style={S.td} onClick={e=>e.stopPropagation()}>
                      <ActionDropdown
                        client={c}
                        inviteStatus={inviteStatus[c.firestoreId]}
                        onEdit={(action)=>{
                          if(action==="inactive") archiveClient(c.firestoreId,"Inactive");
                          else if(action==="archive") archiveClient(c.firestoreId,"Archived");
                          else setModal(c);
                        }}
                        onDelete={()=>deleteClient(c.firestoreId,c.name)}
                        onInvite={()=>inviteClient(c)}
                        onInvoice={()=>setQuickInvoiceClient(c)}
                        onDocRequest={()=>setDocRequestClient(c)}
                        onMessage={()=>{ if(onNavigateToMessages) onNavigateToMessages(c.name); }}
                        onDocRequest={()=>setDocRequestClient(c)}
                        onMessage={()=>{ if(onNavigateToMessages) onNavigateToMessages(c.name); }}
                      />
                    </td>
                  </tr>
                  {expandedId===c.firestoreId&&(
                    <tr>
                      <td colSpan={8} style={{padding:"10px 16px 14px",background:"#fafaf8",borderBottom:"0.5px solid #e0dfd8"}}>
                        <div style={{display:"flex",gap:24,fontSize:12,marginBottom:8}}>
                          <div><span style={{color:"#888"}}>Email: </span>{c.email||"—"}</div>
                          <div><span style={{color:"#888"}}>Phone: </span>{c.phone||"—"}</div>
                          <div><span style={{color:"#888"}}>Location: </span>{c.cityState||"—"}</div>
                          {c.notes&&<div><span style={{color:"#888"}}>Notes: </span>{c.notes}</div>}
                          {c.portalInvitedAt&&<div><span style={{color:"#888"}}>Invited: </span>{new Date(c.portalInvitedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
